import { Suspense, useEffect, useState } from 'react';

import { Tab } from '@headlessui/react';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconHouseFilled, IconSync } from '@iwoca/icons';
import { Card } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { Divider, Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DrawdownPanel from './DrawdownPanel/DrawdownPanel';
import { MoreFundsTab } from './MoreFundsTab/MoreFundsTab';
import { getTopUpStatus } from './NewOutstandingView.helpers';
import * as selectors from '../accountsSelector';
import styles from './NewOutstandingView.module.css';
import OverviewPanel from './OverviewPanel/OverviewPanel';
import RequestMoreFundsPanel from './RequestMoreFundsPanel/RequestMoreFundsPanel';
import TopUpStatusAlertBoxes from './TopUpStatusAlertBoxes/TopUpStatusAlertBoxes';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { isMobileWidth } from '../../../iwMedia/BreakpointHelper';
import { TProductType } from '../../dashboard/Dashboard.types';
import { FundingProgressIndicator } from '../../funding/fundingSteps/FundingProgessIndicator/FundingProgressIndicator';
import { LoanInformationHeader } from '../dashboardComponents/LoanInformationHeader/LoanInformationHeader';
import { FAQs } from '../dashboardWidgets/FAQs/FAQs';
import { TabularRepaymentsPanel } from '../dashboardWidgets/TabularRepaymentsPanel/TabularRepaymentsPanel';

type OwnProps = {
    productType: TProductType;
    accountId: string;
};

const mapStateToProps = (state: RootState, { productType }: OwnProps) => {
    return {
        loanDuration: selectors.getLoanDurationByMonth(state, productType),
        topUpStatus: getTopUpStatus(state, productType)
    };
};
type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const NewOutstandingView = ({
    productType,
    accountId,
    topUpStatus
}: OwnProps & PropsFromRedux) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isTopUpApproved = topUpStatus === 'TOPUP_APPROVED';
    const isPreApproved = topUpStatus === 'PRE_APPROVED';
    const showDrawdownPanel =
        isTopUpApproved ||
        topUpStatus === 'INTEREST_ONLY_EXTENSION_APPROVED' ||
        topUpStatus === 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS';
    const isMoreFundsTabSelected = selectedIndex === 1;
    const hideFAQs =
        isMoreFundsTabSelected && (isPreApproved || isTopUpApproved);
    const history = useHistory();

    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: accountId,
            // @ts-expect-error: missing term loan type
            product_type: productType,
            dashboard_state: 'ongoing',
            topup_status: topUpStatus
        });
    }, [accountId, productType, topUpStatus]);

    const showTopupProgressBar =
        getCountry() === 'UK' && isMoreFundsTabSelected;

    const trackDashboardTabClicked = (tabName: string) => {
        tracking.ampli.dashboardTabClicked({
            account_id: accountId,
            tab_name: tabName,
            // @ts-expect-error
            topup_status: topUpStatus
        });
    };

    const redirectToApplyFlexiLoan = () => {
        history.push(
            `/apply-flexi-loan/?topup=true${
                productType === 'flexi24' ? '&product_type=flexi24' : ''
            }`
        );
    };

    return (
        // pb-[125px] ensures the mobile menu doesn't block any content
        <div className="mx-auto mt-l max-w-3xl px-2xs pb-[125px] min-m:pb-xl">
            {(!isMobileWidth() || !isMoreFundsTabSelected) && (
                <ErrorBoundary
                    fallback={() => <TopUpStatusAlertBoxesLoading />}
                >
                    <Suspense fallback={<TopUpStatusAlertBoxesLoading />}>
                        <TopUpStatusAlertBoxes
                            productType={productType}
                            accountId={accountId}
                            onAccessFundsClick={() => setSelectedIndex(1)}
                            redirectToApplyFlexiLoan={redirectToApplyFlexiLoan}
                        />
                    </Suspense>
                </ErrorBoundary>
            )}
            {showTopupProgressBar && <FundingProgressIndicator step={1} />}
            <Card className="mt-s">
                <LoanInformationHeader accountId={accountId} />

                <Tab.Group
                    selectedIndex={selectedIndex}
                    onChange={setSelectedIndex}
                >
                    <Tab.List className={styles.tabsList}>
                        <Tab
                            className={styles.tab}
                            onClick={() => trackDashboardTabClicked('Overview')}
                        >
                            <IconHouseFilled className={styles.icon} />{' '}
                            <FormattedMessage
                                id="_.outstanding_view.overviewTab"
                                defaultMessage="Overview"
                            />
                        </Tab>

                        <MoreFundsTab
                            accountId={accountId}
                            topUpStatus={topUpStatus}
                            redirectToApplyFlexiLoan={redirectToApplyFlexiLoan}
                            trackTabClicked={() =>
                                trackDashboardTabClicked('More funds')
                            }
                        />

                        <Tab
                            className={styles.tab}
                            onClick={() =>
                                trackDashboardTabClicked('Repayments')
                            }
                        >
                            <IconSync className={styles.icon} />{' '}
                            <FormattedMessage
                                id="_.outstanding_view.repaymentsTab"
                                defaultMessage="Repayments"
                            />
                        </Tab>
                    </Tab.List>

                    <div className="w-full pt-l">
                        <Tab.Panels>
                            <Tab.Panel>
                                <OverviewPanel accountId={accountId} />
                            </Tab.Panel>

                            {showDrawdownPanel ? (
                                <Tab.Panel>
                                    <DrawdownPanel accountId={accountId} />
                                </Tab.Panel>
                            ) : (
                                <Tab.Panel>
                                    <RequestMoreFundsPanel
                                        accountId={accountId}
                                        productType={productType}
                                    />
                                </Tab.Panel>
                            )}

                            <Tab.Panel>
                                <TabularRepaymentsPanel accountId={accountId} />
                            </Tab.Panel>
                        </Tab.Panels>

                        <Divider />

                        {!hideFAQs && <FAQs accountId={accountId} />}
                    </div>
                </Tab.Group>
            </Card>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(NewOutstandingView);

const TopUpStatusAlertBoxesLoading = () => {
    return (
        <div className="space-y-s" data-testid="topup-alert-loading">
            <Skeleton className="bg-structure-100" />
            <Skeleton className="bg-structure-100" />
        </div>
    );
};
